import { useFormikContext } from "formik"
import { useEffect, useMemo, useState } from "react"
import { Alert, Button, Stack, Typography } from "@mui/material"
import { compare } from "dinero.js"
import { makeStyles } from "tss-react/mui"
import { enqueueSnackbar } from "notistack"

import { ProductStockInfo, ProductsTableItem } from "../../../../../../modules/merchant/admin/products/productsTypes"
import {
    DeliveryTimesField,
    DeliveryWithCart,
    DynamicConfigKey,
    isLegacyExtraServiceOrderLine,
    isProductWishOrderLine,
    MeetingServiceTabValues,
    ProductWishOrderLine,
} from "../../form/formTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { EditingWish, ProductWishDialog } from "../../shop/productWish/ProductWishDialog"
import { useTimeout } from "../../../../../../shared/hooks/useTimeout"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { toCurrency, zeroDinero } from "../../../../../../modules/currency/currencyLib"
import { buildInitialQuantityKey, calculateTotalAmountFromDeliveries, exceedsInitialAmount } from "./helpers"
import { useRefundSnapshot } from "./RefundSnapshotContext"
import { INVALID_DELIVERIES } from "../../form/helpers"
import { checkProductStock } from "../../../../productAvailability/stockHelpers"
import { CreateOrderDialogMode } from "../../../createOrderTypes"
import { useRemovedServices } from "../messages/RemovedServicesContext"

import { MeetingServiceDelivery } from "./MeetingServiceDelivery"
import { MeetingServiceTimeErrorDialog } from "./MeetingServiceTimeErrorDialog"
import { MeetingServicesPlaceholder } from "./MeetingServicesPlaceholder"
import { OrderSummaryItem } from "./OrderSummaryItem"

const useStyles = makeStyles()((theme) => ({
    footerInfo: {
        fontWeight: 400,
        color: theme.palette.text.secondary,
    },
    errorAlert: {
        marginTop: "0.5rem",
    },
}))

const defaultDialogState = {
    show: false,
    times: [],
}

export type ProductUpdateQuantityProps = Readonly<{
    deliveryTime: string
    productId: string | number
    quantity: number
    isExtraService?: boolean
    isProductWish?: boolean
    stock?: ProductStockInfo
    catalogProducts?: Map<number, ProductsTableItem>
}>

type MeetingServicesProps = Readonly<{
    isRefundOrder: boolean
    isEditable: boolean
    userId: number
    mode?: CreateOrderDialogMode
}>

export function MeetingServices({ isRefundOrder, isEditable, userId, mode }: MeetingServicesProps) {
    const moneyFactory = useMoney({})
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const { removedServices } = useRemovedServices()
    const removedIds = useMemo(() => new Set(removedServices.map((s) => s.id)), [removedServices])

    const { values, setFieldValue, errors, isSubmitting } = useFormikContext<MeetingServiceTabValues>()
    const deliveries: DeliveryTimesField | undefined = values[DynamicConfigKey.MULTIPLE_DELIVERIES]
    const rawDeliveryTimes = deliveries?.deliveryTimes ?? []
    const deliveryDate = deliveries?.deliveryDate ?? null

    const deliveryTimes: DeliveryWithCart[] = useMemo(
        () =>
            removedIds.size === 0
                ? rawDeliveryTimes
                : rawDeliveryTimes.map((d) => ({
                      ...d,
                      orderLines: d.orderLines.filter(
                          (line) => !line.isExtraService || !removedIds.has(Number(line.id)),
                      ),
                  })),
        [rawDeliveryTimes, removedIds],
    )
    const invalidTimes = errors[DynamicConfigKey.MULTIPLE_DELIVERIES]
    const initAmount = values.initAmount

    const initialRefundQuantities = useRefundSnapshot()
    const [openDialog, setOpenDialog] = useState(false)
    const [editingWish, setEditingWish] = useState<EditingWish | null>(null)
    const [showInvalidTimesModal, setShowInvalidTimesModal] = useState<{ show: boolean; times: string[] }>(
        defaultDialogState,
    )
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    useEffect(() => {
        if (isSubmitting && typeof invalidTimes === "string" && invalidTimes.startsWith(INVALID_DELIVERIES)) {
            const times = invalidTimes.replace(INVALID_DELIVERIES, "").split(",") || []
            if (times.length > 0) {
                setShowInvalidTimesModal({
                    show: true,
                    times: times,
                })
            }
        }
    }, [isSubmitting, invalidTimes])

    // Auto-hide error message after 5 seconds
    useTimeout(() => setErrorMessage(null), errorMessage ? 5000 : null)

    const onUpdateProductQuantity = ({
        deliveryTime,
        productId,
        quantity,
        isExtraService,
        isProductWish,
        stock,
        catalogProducts,
    }: ProductUpdateQuantityProps) => {
        // Find current quantity before update
        let currentQuantity = 0
        let isWishLine = false
        const currentDelivery = deliveryTimes.find((delivery) => delivery.time === deliveryTime)
        if (currentDelivery) {
            const currentProduct = currentDelivery.orderLines.find(
                (item) => item.id === productId && item.isExtraService === isExtraService,
            )
            if (currentProduct && isLegacyExtraServiceOrderLine(currentProduct)) return
            currentQuantity = currentProduct?.quantity ?? 0
            isWishLine = currentProduct ? isProductWishOrderLine(currentProduct) : false
        }

        const isQuantityIncreasing = quantity > currentQuantity

        // Validate that a credited quantity doesn't exceed the initial quantity per product.
        if (isRefundOrder && isQuantityIncreasing) {
            const key = buildInitialQuantityKey({ deliveryTime, productId, isExtraService })
            const initialQuantity = initialRefundQuantities[key]
            // a line added inside the credit note has no initial quantity — only the total cap applies
            const isAddedInCreditNote = isWishLine && initialQuantity === undefined

            if (!isAddedInCreditNote && quantity > (initialQuantity ?? 0)) {
                setErrorMessage(l10n.getString("create-order-refund-quantity-exceeds-initial-error"))
                return
            }
        }

        // Validate stock availability when increasing quantity (non-refund orders)
        if (!isRefundOrder && isQuantityIncreasing && stock) {
            const stockCheck = checkProductStock(stock, quantity)

            if (!stockCheck.allowed) {
                const messageKey =
                    stockCheck.reason === "daily-limit"
                        ? "product-daily-limit-exceeded-snackbar"
                        : "product-stock-limit-exceeded-snackbar"
                enqueueSnackbar(l10n.getString(messageKey), { variant: "warning" })
                return
            }
        }

        const updatedDeliveries = {
            ...deliveries,
            deliveryTimes: deliveryTimes.map((delivery) => {
                if (delivery.time === deliveryTime) {
                    const current = delivery.orderLines.findIndex(
                        (item) => item.id === productId && item.isExtraService === isExtraService,
                    )
                    let newOrderLines = [...delivery.orderLines]
                    if (quantity > 0 || (isRefundOrder && !isProductWish)) {
                        if (current !== -1) {
                            newOrderLines[current] = { ...newOrderLines[current], quantity }
                        }
                    } else {
                        newOrderLines = newOrderLines.filter(
                            (item) => !(item.id === productId && item.isExtraService === isExtraService),
                        )
                    }
                    return { ...delivery, orderLines: newOrderLines }
                }
                return delivery
            }),
        }

        // Validate that refund order total doesn't exceed initial amount when increasing quantity
        if (
            isRefundOrder &&
            initAmount &&
            deliveries &&
            isQuantityIncreasing &&
            exceedsInitialAmount(updatedDeliveries.deliveryTimes, initAmount)
        ) {
            setErrorMessage(l10n.getString("create-order-refund-amount-exceeds-initial-error"))
            return
        }

        // Clear error message when quantity is successfully updated
        setErrorMessage(null)
        setFieldValue(DynamicConfigKey.MULTIPLE_DELIVERIES, updatedDeliveries)
    }

    const totalAmount = useMemo(
        () => (deliveries ? calculateTotalAmountFromDeliveries(deliveries.deliveryTimes) : null),
        [deliveries],
    )

    const isTotalAmountZero = useMemo(() => {
        if (!totalAmount) return true
        const zero = zeroDinero(toCurrency("DKK"))
        return compare(totalAmount, zero) === 0
    }, [totalAmount])

    const formattedDate = deliveryDate && dateTimeFactory.fromISO(deliveryDate).toFormat("d MMMM")
    const totalProductsCount = deliveryTimes.reduce((sum, delivery) => sum + delivery.orderLines.length, 0)
    const hasProducts = totalProductsCount > 0
    const canAddOrderLine = isEditable && isRefundOrder && Boolean(formattedDate) && deliveryTimes.length > 0

    const handleEditWish = (deliveryTime: string, line: ProductWishOrderLine) => {
        setEditingWish({ deliveryTime, line })
    }

    const handleOpenDialog = () => {
        const activeTime = deliveries?.activeTime
        if (!activeTime) {
            const firstTime = deliveryTimes[0]?.time
            if (firstTime) setFieldValue(`${DynamicConfigKey.MULTIPLE_DELIVERIES}.activeTime`, firstTime)
        }
        setOpenDialog(true)
    }

    const handleDialogClose = () => {
        if (showInvalidTimesModal.times.length > 0) {
            const firstInvalidTime = showInvalidTimesModal.times[0]
            setFieldValue(`${DynamicConfigKey.MULTIPLE_DELIVERIES}.activeTime`, firstInvalidTime)
        }
        setShowInvalidTimesModal(defaultDialogState)
    }

    return (
        <>
            <LocalizedStrict id="create-order-order-summary-meeting-services" attrs={{ title: true }}>
                <OrderSummaryItem title="Meeting services">
                    <Stack gap="1.5rem">
                        {formattedDate ? (
                            deliveryTimes.map((delivery, index) => (
                                <MeetingServiceDelivery
                                    key={index}
                                    serialNumber={index + 1}
                                    deliveryTime={delivery.time || ""}
                                    deliveryDate={deliveryDate}
                                    orderLines={delivery.orderLines}
                                    isRefundOrder={isRefundOrder}
                                    onUpdateQuantity={onUpdateProductQuantity}
                                    onEditWish={handleEditWish}
                                    isEditable={isEditable}
                                    userId={userId}
                                    mode={mode}
                                />
                            ))
                        ) : (
                            <MeetingServicesPlaceholder />
                        )}

                        {canAddOrderLine && (
                            <Stack direction="row">
                                <LocalizedStrict id="create-order-order-summary-meeting-services-add-order-line-button">
                                    <Button
                                        type="button"
                                        variant="text"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={handleOpenDialog}
                                    >
                                        + Add order line
                                    </Button>
                                </LocalizedStrict>
                            </Stack>
                        )}

                        {hasProducts && (
                            <Stack gap="0.5rem">
                                {totalAmount && (
                                    <Stack
                                        flexDirection="row"
                                        gap="1rem"
                                        justifyContent="space-between"
                                        alignItems="flex-end"
                                    >
                                        <LocalizedStrict id="create-order-order-summary-meeting-services-total">
                                            <Typography variant="h6" className={classes.footerInfo}>
                                                Total
                                            </Typography>
                                        </LocalizedStrict>
                                        <Typography variant="h4">
                                            {isRefundOrder && !isTotalAmountZero && "-"}
                                            {moneyFactory.format(totalAmount)}
                                        </Typography>
                                    </Stack>
                                )}
                                {errorMessage && (
                                    <Alert
                                        severity="error"
                                        className={classes.errorAlert}
                                        onClose={() => setErrorMessage(null)}
                                    >
                                        {errorMessage}
                                    </Alert>
                                )}
                            </Stack>
                        )}
                        {showInvalidTimesModal.show && (
                            <MeetingServiceTimeErrorDialog
                                show={showInvalidTimesModal.show}
                                times={showInvalidTimesModal.times}
                                handleDialogClose={handleDialogClose}
                            />
                        )}
                    </Stack>
                </OrderSummaryItem>
            </LocalizedStrict>
            {openDialog && <ProductWishDialog open={openDialog} onClose={() => setOpenDialog(false)} />}
            {editingWish && <ProductWishDialog open editingWish={editingWish} onClose={() => setEditingWish(null)} />}
        </>
    )
}
